/**
 * Tenant List Component
 * Data table displaying registered tenants with search, filter, pagination, and row selection.
 *
 * Features:
 * - Row-level checkboxes for selecting individual tenants
 * - Header checkbox for "select all" functionality
 * - Search and status filtering
 * - Pagination with page size of 20
 * - Tenant registration dialog
 * - Access verification for registered tenants
 */

import React, { useState, useEffect, useMemo } from 'react';
import {
  Avatar,
  Button,
  Dropdown,
  Option,
  TableCellLayout,
  TableColumnDefinition,
  createTableColumn,
  Badge,
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogContent,
  DialogActions,
  Text,
  Spinner,
  makeStyles,
} from '@fluentui/react-components';
import {
  Add20Regular,
  ArrowClockwise20Regular,
  Archive20Regular,
  Delete20Regular,
  Delete24Regular,
  Dismiss20Regular,
  Edit20Regular,
  Open20Regular,
  Pause20Regular,
  Play20Regular,
  ShieldCheckmark20Regular,
} from '@fluentui/react-icons';
import { TenantApiService } from '../services/tenantApi';
import { TenantRequestApiService } from '../services/tenantRequestApi';
import { TenantRegistrationForm } from '../components/TenantManagement/TenantRegistrationForm';
import { TenantFulfillmentModal } from '../components/TenantManagement/TenantFulfillmentModal';
import { AdminConsentModal, AdminConsentApp } from '../components/TenantManagement/AdminConsentModal';
import { useTenantContext } from '../contexts/TenantContext';
import { Alert } from '../components/common/Alert';
import { TenantStatus } from '../types/tenant';
import type { TenantListItem } from '../types/tenant';
import type { TenantRequest, TenantRegistrationRequest, FulfillmentChecklist } from '../types/tenantRequest';
import { createTextColumn } from '../components/common/DataGrid/factories/createTextColumn';
import { createColumnWithRowActions, type ActionConfig } from '../components/layouts/factories/columns';
import {
  PageLayout,
  DataGridCard,
  DataGridToolbar,
  ToolbarButton,
  ToolbarSearch,
} from '../components/layouts';
import { useTenants } from '../hooks/useTenants';
import { usePendingRequests } from '../hooks/usePendingRequests';

const useStyles = makeStyles({
  statusFilter: {
    minWidth: '150px',
  },
  statusBadge: {
    textTransform: 'capitalize',
  },
});

/**
 * Get status badge color
 */
const getStatusColor = (status: TenantStatus) => {
  switch (status) {
    case TenantStatus.HOME:
      return 'brand' as const;
    case TenantStatus.MANAGED:
      return 'success' as const;
    case TenantStatus.PAUSED:
      return 'warning' as const;
    case TenantStatus.ARCHIVED:
      return 'danger' as const;
    default:
      return 'informative' as const;
  }
};

/**
 * Tenant List Component
 * Displays data table of registered tenants with management actions.
 *
 * @example
 * ```tsx
 * <TenantList />
 * ```
 */
export const TenantManagementPage: React.FC = () => {
  const styles = useStyles();
  const { refreshTenantList } = useTenantContext();

  // Tab state with localStorage persistence
  const [selectedTab, setSelectedTab] = useState<'tenants' | 'requests'>(() => {
    const stored = localStorage.getItem('tenantManagementSelectedTab');
    return (stored === 'requests' ? 'requests' : 'tenants') as 'tenants' | 'requests';
  });

  // Separate search queries for each tab
  const [tenantSearchQuery, setTenantSearchQuery] = useState('');
  const [requestSearchQuery, setRequestSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<TenantStatus | 'all'>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [requestsPage, setRequestsPage] = useState(1);
  const pageSize = 20;

  // React Query hooks for data fetching
  const {
    tenants,
    totalPages,
    totalTenants,
    isLoading,
    isFetching,
    error: tenantsError,
    refetch: refetchTenants,
  } = useTenants({
    page: currentPage,
    pageSize,
    search: tenantSearchQuery || undefined,
    status: statusFilter !== 'all' ? statusFilter : undefined,
  });

  const {
    pendingRequests,
    isLoading: pendingRequestsLoading,
    isFetching: isFetchingPendingRequests,
    refetch: refetchPendingRequests,
  } = usePendingRequests();

  // Filter pending requests by search query
  const filteredRequests = useMemo(() => {
    if (!requestSearchQuery.trim()) {
      return pendingRequests;
    }
    const query = requestSearchQuery.toLowerCase();
    return pendingRequests.filter((request) => {
      // Get display name based on request type
      const displayName = request.request_type === 'status_change'
        ? request.target_tenant_name || ''
        : request.requested_name || '';
      return (
        displayName.toLowerCase().includes(query) ||
        request.organization_name.toLowerCase().includes(query) ||
        request.requested_by_name.toLowerCase().includes(query) ||
        (request.notes && request.notes.toLowerCase().includes(query))
      );
    });
  }, [pendingRequests, requestSearchQuery]);

  // Calculate pagination for filtered requests
  const requestsTotalPages = Math.ceil(filteredRequests.length / pageSize) || 1;
  const paginatedRequests = useMemo(() => {
    const startIndex = (requestsPage - 1) * pageSize;
    return filteredRequests.slice(startIndex, startIndex + pageSize);
  }, [filteredRequests, requestsPage, pageSize]);

  // Reset to page 1 when search query changes
  useEffect(() => {
    setRequestsPage(1);
  }, [requestSearchQuery]);

  // Convert error object to string for display
  const error = tenantsError?.message ?? null;

  // Mutation error state (for actions like reject, start fulfillment, etc.)
  const [mutationError, setMutationError] = useState<string | null>(null);

  const [showAddDialog, setShowAddDialog] = useState(false);
  const [updatingTenant, setUpdatingTenant] = useState<string | null>(null);

  // Row selection state
  const [selectedTenants, setSelectedTenants] = useState<Set<string>>(new Set());

  // Bulk delete state
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState<boolean>(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // Single delete state (for actions menu)
  const [singleDeleteDialogOpen, setSingleDeleteDialogOpen] = useState<boolean>(false);
  const [tenantToDelete, setTenantToDelete] = useState<TenantListItem | null>(null);
  const [isSingleDeleting, setIsSingleDeleting] = useState<boolean>(false);

  // Bulk delete loading state
  const [isBulkDeleting, setIsBulkDeleting] = useState<boolean>(false);

  // Edit tenant dialog state
  const [editDialogOpen, setEditDialogOpen] = useState<boolean>(false);
  const [tenantToEdit, setTenantToEdit] = useState<TenantListItem | null>(null);

  // Admin consent dialog state
  const [adminConsentDialogOpen, setAdminConsentDialogOpen] = useState<boolean>(false);
  const [adminConsentTenant, setAdminConsentTenant] = useState<TenantListItem | null>(null);
  const [adminConsentApps, setAdminConsentApps] = useState<AdminConsentApp[]>([]);

  // Pending requests UI state
  const [selectedRequests, setSelectedRequests] = useState<Set<number>>(new Set());

  // Fulfillment modal state
  const [fulfillmentModalOpen, setFulfillmentModalOpen] = useState<boolean>(false);
  const [fulfillmentRequest, setFulfillmentRequest] = useState<TenantRequest | null>(null);

  // Fulfillment registration flow state (when Create Managed Tenant is clicked)
  const [fulfillingRequestId, setFulfillingRequestId] = useState<number | null>(null);
  const [fulfillmentPrefillData, setFulfillmentPrefillData] = useState<{
    tenant_id: string;
    tenant_name: string;
    domain: string;
    notes: string;
    organization_id?: number;
  } | null>(null);

  /**
   * Handle bulk delete initiation
   */
  const handleBulkDelete = () => {
    setDeleteError(null); // Clear any previous errors
    setBulkDeleteDialogOpen(true);
  };

  /**
   * Handle bulk delete confirmation
   */
  const handleConfirmBulkDelete = async () => {
    const selectedTenantIds = Array.from(selectedTenants);
    const selectedRequestIds = Array.from(selectedRequests);
    setDeleteError(null);
    setIsBulkDeleting(true);

    let hasErrors = false;
    let errorMessage = '';

    try {
      // Delete selected tenants
      for (const tenantId of selectedTenantIds) {
        try {
          await TenantApiService.deactivateTenant(tenantId);
        } catch (err) {
          hasErrors = true;
          if (err instanceof Error) {
            // Check for permission error
            if (err.message.includes('403') || err.message.includes('Forbidden')) {
              errorMessage = 'You do not have permission to delete tenants. Superadmin access is required.';
            } else {
              errorMessage = err.message;
            }
          } else {
            errorMessage = 'Failed to delete one or more tenants';
          }
        }
      }

      // Delete selected requests
      for (const requestId of selectedRequestIds) {
        try {
          await TenantRequestApiService.deleteRequest(requestId);
        } catch (err) {
          hasErrors = true;
          if (err instanceof Error) {
            if (err.message.includes('403') || err.message.includes('Forbidden')) {
              errorMessage = 'You do not have permission to delete requests.';
            } else {
              errorMessage = err.message;
            }
          } else {
            errorMessage = 'Failed to delete one or more requests';
          }
        }
      }

      setBulkDeleteDialogOpen(false);

      if (hasErrors) {
        setDeleteError(errorMessage);
      } else {
        setSelectedTenants(new Set());
        setSelectedRequests(new Set());
      }

      await refetchTenants();
      await refetchPendingRequests();
      await refreshTenantList();
    } finally {
      setIsBulkDeleting(false);
    }
  };

  /**
   * Handle single tenant delete initiation (from actions menu)
   */
  const handleSingleDelete = (tenant: TenantListItem) => {
    setDeleteError(null);
    setTenantToDelete(tenant);
    setSingleDeleteDialogOpen(true);
  };

  /**
   * Handle single tenant delete confirmation
   */
  const handleConfirmSingleDelete = async () => {
    if (!tenantToDelete) return;

    setDeleteError(null);
    setIsSingleDeleting(true);

    try {
      await TenantApiService.deactivateTenant(tenantToDelete.tenant_id);
      setSingleDeleteDialogOpen(false);
      setTenantToDelete(null);
    } catch (err) {
      if (err instanceof Error) {
        if (err.message.includes('403') || err.message.includes('Forbidden')) {
          setDeleteError('You do not have permission to delete tenants. Superadmin access is required.');
        } else {
          setDeleteError(err.message);
        }
      } else {
        setDeleteError('Failed to delete tenant');
      }
      setSingleDeleteDialogOpen(false);
    } finally {
      setIsSingleDeleting(false);
    }

    await refetchTenants();
    await refreshTenantList();
  };

  /**
   * Handle reject request action
   */
  const handleRejectRequest = async (request: TenantRequest) => {
    try {
      await TenantRequestApiService.rejectRequest(request.id, {});
      await refetchPendingRequests(); // Refresh pending requests
    } catch (err) {
      setMutationError(err instanceof Error ? err.message : 'Failed to reject request');
    }
  };

  /**
   * Handle delete request action
   */
  const handleDeleteRequest = async (request: TenantRequest) => {
    try {
      await TenantRequestApiService.deleteRequest(request.id);
      setSelectedRequests((prev) => {
        const newSet = new Set(prev);
        newSet.delete(request.id);
        return newSet;
      });
      await refetchPendingRequests(); // Refresh pending requests
    } catch (err) {
      setMutationError(err instanceof Error ? err.message : 'Failed to delete request');
    }
  };

  /**
   * Handle start action (starts fulfillment workflow for pending requests)
   */
  const handleStartRequest = async (request: TenantRequest) => {
    try {
      await TenantRequestApiService.startFulfillment(request.id);
      setFulfillmentRequest(request);
      setFulfillmentModalOpen(true);
      await refetchPendingRequests(); // Refresh to show in_process status
    } catch (err) {
      setMutationError(err instanceof Error ? err.message : 'Failed to start fulfillment');
    }
  };

  /**
   * Handle resume action (opens modal to continue in_process requests)
   */
  const handleResumeRequest = (request: TenantRequest) => {
    setFulfillmentRequest(request);
    setFulfillmentModalOpen(true);
  };

  /**
   * Handle save checklist (saves progress without completing)
   */
  const handleSaveChecklist = async (checklist: FulfillmentChecklist) => {
    if (!fulfillmentRequest) return;

    try {
      await TenantRequestApiService.updateChecklist(fulfillmentRequest.id, checklist);
      await refetchPendingRequests(); // Refresh to get updated checklist
    } catch (err) {
      setMutationError(err instanceof Error ? err.message : 'Failed to save checklist');
    }
  };

  /**
   * Handle complete fulfillment (opens add tenant dialog with pre-filled data)
   * Instead of directly calling the API, we open the registration form with
   * the tenant data pre-filled so the admin consent flow can be handled properly.
   */
  const handleCompleteFulfillment = (tenantData: TenantRegistrationRequest) => {
    if (!fulfillmentRequest) return;

    // Store the request ID so we can link the tenant after registration
    setFulfillingRequestId(fulfillmentRequest.id);

    // Pre-fill the registration form with data from the fulfillment modal
    // Notes and organization_id come from the original request, not tenantData
    // tenant_id is optional here — it's auto-discovered from domain by the backend
    setFulfillmentPrefillData({
      tenant_id: tenantData.tenant_id || '',
      tenant_name: tenantData.tenant_name,
      domain: tenantData.domain,
      notes: fulfillmentRequest.notes || '',
      organization_id: fulfillmentRequest.organization_id,
    });

    // Close the fulfillment modal
    setFulfillmentModalOpen(false);
    setFulfillmentRequest(null);

    // Open the add tenant dialog
    setShowAddDialog(true);
  };

  /**
   * Handle tenant status change
   */
  const handleStatusChange = async (tenantId: string, newStatus: TenantStatus) => {
    setUpdatingTenant(tenantId);
    setMutationError(null);
    try {
      await TenantApiService.updateTenant(tenantId, { status: newStatus });
      await refetchTenants(); // Refresh list
      await refreshTenantList(); // Update context
    } catch (err) {
      setMutationError(err instanceof Error ? err.message : 'Failed to update tenant status');
    } finally {
      setUpdatingTenant(null);
    }
  };

  /**
   * Handle tenant registration success
   * If we're fulfilling a request, link the newly created tenant to the request.
   */
  const handleRegistrationSuccess = async (tenantDbId?: number) => {
    setShowAddDialog(false);

    // If we were fulfilling a request, link the tenant to the request
    if (fulfillingRequestId && tenantDbId) {
      try {
        await TenantRequestApiService.linkTenantToRequest(fulfillingRequestId, tenantDbId);
        await refetchPendingRequests(); // Refresh pending requests (request is now fulfilled)
      } catch (err) {
        setMutationError(err instanceof Error ? err.message : 'Failed to link tenant to request');
      }
    }

    // Clear fulfillment state
    setFulfillingRequestId(null);
    setFulfillmentPrefillData(null);

    await refetchTenants();
    await refreshTenantList(); // Update context
  };

  /**
   * Handle edit tenant initiation from actions menu
   */
  const handleEditTenant = (tenant: TenantListItem) => {
    setTenantToEdit(tenant);
    setEditDialogOpen(true);
  };

  /**
   * Handle edit tenant success
   */
  const handleEditSuccess = async () => {
    setEditDialogOpen(false);
    setTenantToEdit(null);
    await refetchTenants();
    await refreshTenantList();
  };

  /**
   * Handle admin consent initiation from actions menu
   * Gets the list of apps from a fast endpoint (no Graph API calls)
   */
  const handleAdminConsent = async (tenant: TenantListItem) => {
    setAdminConsentTenant(tenant);
    setMutationError(null);

    try {
      // Get apps from fast endpoint (no Graph API verification)
      const response = await TenantApiService.getConsentApps();

      // Map apps to AdminConsentApp format with descriptions
      const apps: AdminConsentApp[] = response.apps.map((app) => ({
        clientId: app.client_id,
        name: app.name,
        description: app.name === 'IRPilot Service App'
          ? 'Grants permission for IRPilot to manage users, licenses, and security policies in this tenant.'
          : 'Enables users in this tenant to sign in to IRPilot using their Entra ID account.',
      }));

      setAdminConsentApps(apps);
      setAdminConsentDialogOpen(true);
    } catch (err) {
      setMutationError(err instanceof Error ? err.message : 'Failed to get consent apps');
      setAdminConsentTenant(null);
    }
  };

  /**
   * Handle successful admin consent verification
   */
  const handleAdminConsentVerifySuccess = () => {
    setAdminConsentDialogOpen(false);
    setAdminConsentTenant(null);
    setAdminConsentApps([]);
    // Refresh tenant list to show updated status
    refetchTenants();
    refreshTenantList();
  };

  /**
   * Close admin consent dialog
   */
  const handleCloseAdminConsentDialog = () => {
    setAdminConsentDialogOpen(false);
    setAdminConsentTenant(null);
    setAdminConsentApps([]);
  };

  /**
   * Define table columns
   * Order: Name, Domain, Tenant ID, Registered, Status, Notes
   */
  const columns: TableColumnDefinition<TenantListItem>[] = [
    // Name column with Avatar and row actions
    createColumnWithRowActions<TenantListItem>({
      columnId: 'name',
      header: 'Name',
      compare: (a, b) => a.tenant_name.localeCompare(b.tenant_name),
      renderContent: (item) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 }}>
          <Avatar
            name={item.tenant_name}
            size={32}
            color="colorful"
          />
          <Text weight="semibold" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {item.tenant_name}
          </Text>
        </div>
      ),
      getActions: (item) => {
        const actions: ActionConfig[] = [];
        // Set to Managed - only show when paused or archived
        if (item.status === 'paused' || item.status === 'archived') {
          actions.push({
            label: 'Set to Managed',
            icon: <Play20Regular />,
            onClick: () => handleStatusChange(item.tenant_id, TenantStatus.MANAGED),
            disabled: updatingTenant === item.tenant_id,
          });
        }
        // Pause Management - only show when managed
        if (item.status === 'managed') {
          actions.push({
            label: 'Pause Management',
            icon: <Pause20Regular />,
            onClick: () => handleStatusChange(item.tenant_id, TenantStatus.PAUSED),
            disabled: updatingTenant === item.tenant_id,
          });
        }
        // Archive Tenant - only show when managed or paused
        if (item.status === 'managed' || item.status === 'paused') {
          actions.push({
            label: 'Archive Tenant',
            icon: <Archive20Regular />,
            onClick: () => handleStatusChange(item.tenant_id, TenantStatus.ARCHIVED),
            disabled: updatingTenant === item.tenant_id,
          });
        }
        // Edit Tenant - always show
        actions.push({
          label: 'Edit Tenant',
          icon: <Edit20Regular />,
          onClick: () => handleEditTenant(item),
        });
        // Admin Consent - always show
        actions.push({
          label: 'Admin Consent',
          icon: <ShieldCheckmark20Regular />,
          onClick: () => handleAdminConsent(item),
        });
        // Delete Tenant - hide for home tenant (cannot be deleted)
        if (item.status !== 'home') {
          actions.push({
            label: 'Delete Tenant',
            icon: <Delete20Regular />,
            onClick: () => handleSingleDelete(item),
          });
        }
        return actions;
      },
      getAriaLabel: (item) => `Actions for ${item.tenant_name}`,
    }),
    // Domain column
    createTextColumn<TenantListItem>({
      columnId: 'domain',
      headerText: 'Domain',
      getValue: (item) => item.domain,
      sortable: true,
    }),
    // Tenant ID column (smaller font to fit GUID)
    createTableColumn<TenantListItem>({
      columnId: 'tenantId',
      compare: (a, b) => a.tenant_id.localeCompare(b.tenant_id),
      renderHeaderCell: () => 'Tenant ID',
      renderCell: (item) => (
        <TableCellLayout>
          <Text size={200} style={{ fontFamily: 'monospace' }}>
            {item.tenant_id}
          </Text>
        </TableCellLayout>
      ),
    }),
    // Registered column
    createTableColumn<TenantListItem>({
      columnId: 'created',
      compare: (a, b) => {
        const dateA = new Date(a.created_at).getTime();
        const dateB = new Date(b.created_at).getTime();
        return dateA - dateB;
      },
      renderHeaderCell: () => 'Registered',
      renderCell: (item) => (
        <TableCellLayout truncate>
          {new Date(item.created_at).toLocaleDateString()}
        </TableCellLayout>
      ),
    }),
    // Status column
    createTableColumn<TenantListItem>({
      columnId: 'status',
      compare: (a, b) => a.status.localeCompare(b.status),
      renderHeaderCell: () => 'Status',
      renderCell: (item) => (
        <TableCellLayout>
          <Badge
            appearance="filled"
            color={getStatusColor(item.status)}
            className={styles.statusBadge}
          >
            {item.status}
          </Badge>
        </TableCellLayout>
      ),
    }),
    // Notes column
    createTextColumn<TenantListItem>({
      columnId: 'notes',
      headerText: 'Notes',
      getValue: (item) => item.notes || '',
      sortable: false,
    }),
  ];

  /**
   * Get display name for a request (handles both new_tenant and status_change types)
   */
  const getRequestDisplayName = (item: TenantRequest): string => {
    if (item.request_type === 'status_change') {
      return item.target_tenant_name || '';
    }
    return item.requested_name || '';
  };

  /**
   * Define request table columns
   */
  const requestColumns: TableColumnDefinition<TenantRequest>[] = [
    createColumnWithRowActions<TenantRequest>({
      columnId: 'name',
      header: 'Name',
      compare: (a, b) => getRequestDisplayName(a).localeCompare(getRequestDisplayName(b)),
      renderContent: (item) => {
        const displayName = getRequestDisplayName(item);
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 }}>
            <Avatar
              name={displayName}
              size={32}
              color="colorful"
            />
            <Text weight="semibold" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {displayName}
            </Text>
          </div>
        );
      },
      getActions: (item) => {
        const actions: ActionConfig[] = [];
        // Start - only for pending requests
        if (item.status === 'pending') {
          actions.push({
            label: 'Start',
            icon: <Play20Regular />,
            onClick: () => handleStartRequest(item),
          });
        }
        // Open - only for in_process requests
        if (item.status === 'in_process') {
          actions.push({
            label: 'Open',
            icon: <ArrowClockwise20Regular />,
            onClick: () => handleResumeRequest(item),
          });
        }
        // Reject - for non-fulfilled requests
        if (item.status !== 'fulfilled') {
          actions.push({
            label: 'Reject',
            icon: <Dismiss20Regular />,
            onClick: () => handleRejectRequest(item),
          });
        }
        // Delete - for non-fulfilled requests
        if (item.status !== 'fulfilled') {
          actions.push({
            label: 'Delete',
            icon: <Delete20Regular />,
            onClick: () => handleDeleteRequest(item),
          });
        }
        return actions;
      },
      getAriaLabel: (item) => `Actions for ${getRequestDisplayName(item)}`,
    }),
    createTextColumn<TenantRequest>({
      columnId: 'requestedBy',
      headerText: 'Requested By',
      getValue: (item) => item.requested_by_name,
      sortable: true,
    }),
    createTextColumn<TenantRequest>({
      columnId: 'organization',
      headerText: 'Organization',
      getValue: (item) => item.organization_name,
      sortable: true,
    }),
    createTableColumn<TenantRequest>({
      columnId: 'date',
      compare: (a, b) => {
        const dateA = new Date(a.requested_at).getTime();
        const dateB = new Date(b.requested_at).getTime();
        return dateA - dateB;
      },
      renderHeaderCell: () => 'Request Time',
      renderCell: (item) => (
        <TableCellLayout truncate>
          {new Date(item.requested_at).toLocaleString()}
        </TableCellLayout>
      ),
    }),
    createTableColumn<TenantRequest>({
      columnId: 'status',
      compare: (a, b) => a.status.localeCompare(b.status),
      renderHeaderCell: () => 'Status',
      renderCell: (item) => (
        <TableCellLayout truncate>
          {item.status.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}
        </TableCellLayout>
      ),
    }),
    createTextColumn<TenantRequest>({
      columnId: 'notes',
      headerText: 'Notes',
      getValue: (item) => item.notes || '',
      sortable: false,
    }),
  ];

  // Toolbar for Managed Tenants tab
  const renderTenantsToolbar = () => (
    <DataGridToolbar>
      <DataGridToolbar.Left>
        <ToolbarButton
          icon={<ArrowClockwise20Regular />}
          onClick={() => {
            refetchTenants();
            refetchPendingRequests();
          }}
          loading={isFetching}
          loadingText="Refreshing..."
          data-testid="refresh-tenants-button"
        >
          Refresh
        </ToolbarButton>
        <Dropdown
          className={styles.statusFilter}
          placeholder="All Status"
          value={statusFilter === 'all' ? 'All Status' : statusFilter}
          onOptionSelect={(_, data) => {
            setStatusFilter((data.optionValue as TenantStatus | 'all') || 'all');
            setCurrentPage(1);
          }}
          data-testid="status-filter"
        >
          <Option value="all">All Status</Option>
          <Option value="home">Home</Option>
          <Option value="managed">Managed</Option>
          <Option value="paused">Paused</Option>
          <Option value="archived">Archived</Option>
        </Dropdown>
      </DataGridToolbar.Left>
      <DataGridToolbar.Right>
        <ToolbarSearch
          placeholder="Search tenants..."
          value={tenantSearchQuery}
          onChange={(value) => {
            setTenantSearchQuery(value);
            setCurrentPage(1);
          }}
          data-testid="tenant-search"
        />
        {tenants.length > 0 && (
          <ToolbarButton
            icon={<Delete24Regular />}
            data-testid="bulk-delete-button"
            onClick={handleBulkDelete}
            disabled={selectedTenants.size === 0}
          >
            {selectedTenants.size > 0 ? `Delete (${selectedTenants.size})` : 'Delete'}
          </ToolbarButton>
        )}
        <ToolbarButton
          appearance="primary"
          icon={<Add20Regular />}
          onClick={() => setShowAddDialog(true)}
          data-testid="add-tenant-button"
        >
          Add Tenant
        </ToolbarButton>
      </DataGridToolbar.Right>
    </DataGridToolbar>
  );

  // Toolbar for Pending Requests tab
  const renderRequestsToolbar = () => (
    <DataGridToolbar>
      <DataGridToolbar.Left>
        <ToolbarButton
          icon={<ArrowClockwise20Regular />}
          onClick={() => {
            refetchTenants();
            refetchPendingRequests();
          }}
          loading={isFetchingPendingRequests}
          loadingText="Refreshing..."
          data-testid="refresh-requests-button"
        >
          Refresh
        </ToolbarButton>
      </DataGridToolbar.Left>
      <DataGridToolbar.Right>
        <ToolbarSearch
          placeholder="Search requests..."
          value={requestSearchQuery}
          onChange={setRequestSearchQuery}
          data-testid="request-search"
        />
        {pendingRequests.length > 0 && (
          <ToolbarButton
            icon={<Delete24Regular />}
            data-testid="bulk-delete-requests-button"
            onClick={handleBulkDelete}
            disabled={selectedRequests.size === 0}
          >
            {selectedRequests.size > 0 ? `Delete (${selectedRequests.size})` : 'Delete'}
          </ToolbarButton>
        )}
        <ToolbarButton
          appearance="primary"
          icon={<Add20Regular />}
          onClick={() => setShowAddDialog(true)}
          data-testid="add-tenant-button"
        >
          Add Tenant
        </ToolbarButton>
      </DataGridToolbar.Right>
    </DataGridToolbar>
  );

  // Handle selection changes from DataGridCard
  const handleTenantSelectionChange = (ids: Set<string | number>) => {
    const stringIds = new Set<string>();
    ids.forEach((id) => stringIds.add(String(id)));
    setSelectedTenants(stringIds);
  };

  // Handle request selection changes from DataGridCard
  const handleRequestSelectionChange = (ids: Set<string | number>) => {
    const numberIds = new Set<number>();
    ids.forEach((id) => numberIds.add(typeof id === 'number' ? id : parseInt(String(id), 10)));
    setSelectedRequests(numberIds);
  };

  // Managed Tenants content
  const renderTenantsContent = () => (
    <>
      {/* Error Messages */}
      {error && (
        <Alert intent="error">
          {error}
        </Alert>
      )}
      {mutationError && (
        <Alert intent="error" data-testid="mutation-error-message">
          {mutationError}
        </Alert>
      )}
      {deleteError && (
        <Alert intent="error" data-testid="delete-error-message">
          {deleteError}
        </Alert>
      )}

      {/* Tenants DataGrid */}
      <DataGridCard
        items={tenants}
        columns={columns}
        getRowId={(item) => item.tenant_id}
        defaultSortState={{ sortColumn: 'name', sortDirection: 'ascending' }}
        toolbar={renderTenantsToolbar()}
        pagination={{
          showing: tenants.length,
          total: totalTenants,
          page: currentPage,
          totalPages: totalPages || 1,
          onPageChange: setCurrentPage,
        }}
        selectable
        selectedIds={selectedTenants}
        onSelectionChange={handleTenantSelectionChange}
        loading={isLoading}
        emptyMessage="No tenants found"
        testId="tenant-table"
      />
    </>
  );

  // Pending Requests content
  const renderRequestsContent = () => (
    <>
      {/* Error Messages */}
      {mutationError && (
        <Alert intent="error" data-testid="mutation-error-message">
          {mutationError}
        </Alert>
      )}
      {deleteError && (
        <Alert intent="error" data-testid="delete-error-message">
          {deleteError}
        </Alert>
      )}

      {/* Requests DataGrid */}
      <DataGridCard
        items={paginatedRequests}
        columns={requestColumns}
        getRowId={(item) => item.id}
        defaultSortState={{ sortColumn: 'name', sortDirection: 'ascending' }}
        toolbar={renderRequestsToolbar()}
        pagination={{
          showing: paginatedRequests.length,
          total: filteredRequests.length,
          page: requestsPage,
          totalPages: requestsTotalPages,
          onPageChange: setRequestsPage,
        }}
        selectable
        selectedIds={selectedRequests}
        onSelectionChange={handleRequestSelectionChange}
        loading={pendingRequestsLoading}
        emptyMessage="No pending requests"
        testId="pending-requests-table"
      />
    </>
  );

  return (
    <PageLayout
      title="Tenant Management"
      tabs={[
        { value: 'tenants', label: 'Managed Tenants' },
        { value: 'requests', label: `Pending Requests${pendingRequests.length > 0 ? ` (${pendingRequests.length})` : ''}` },
      ]}
      selectedTab={selectedTab}
      onTabChange={(value) => {
        setSelectedTab(value as 'tenants' | 'requests');
        localStorage.setItem('tenantManagementSelectedTab', value);
      }}
      headerActions={
        <Button
          appearance="subtle"
          icon={<Open20Regular />}
          as="a"
          href="https://portal.azure.com/#view/Microsoft_AAD_IAM/DirectorySwitchBlade/subtitle"
          target="_blank"
          rel="noopener noreferrer"
          data-testid="azure-portal-link"
        >
          Create tenant in Azure
        </Button>
      }
      testId="tenant-list"
    >
      {selectedTab === 'tenants' && renderTenantsContent()}
      {selectedTab === 'requests' && renderRequestsContent()}

      {/* Bulk Delete Confirmation Dialog */}
      <Dialog open={bulkDeleteDialogOpen} onOpenChange={(_, data) => {
        if (!data.open && !isBulkDeleting) {
          setBulkDeleteDialogOpen(false);
        }
      }}>
        <DialogSurface data-testid="bulk-delete-dialog">
          <DialogBody>
            <DialogTitle>Confirm Bulk Delete</DialogTitle>
            <DialogContent>
              <Text>
                Are you sure you want to delete {selectedTenants.size + selectedRequests.size} item{selectedTenants.size + selectedRequests.size !== 1 ? 's' : ''}? This action cannot be undone.
              </Text>

              {/* Show selected tenants */}
              {selectedTenants.size > 0 && (
                <div style={{ marginTop: '16px' }}>
                  <Text weight="semibold">Tenants to be deleted ({selectedTenants.size}):</Text>
                  <ul style={{ marginTop: '8px', marginBottom: '0' }}>
                    {Array.from(selectedTenants).map(tenantId => {
                      const tenant = tenants.find(t => t.tenant_id === tenantId);
                      if (tenant) {
                        return (
                          <li key={tenantId} style={{ marginBottom: '4px' }}>
                            {tenant.tenant_name}
                          </li>
                        );
                      }
                      return null;
                    })}
                  </ul>
                </div>
              )}

              {/* Show selected requests */}
              {selectedRequests.size > 0 && (
                <div style={{ marginTop: '16px' }}>
                  <Text weight="semibold">Tenant requests to be deleted ({selectedRequests.size}):</Text>
                  <ul style={{ marginTop: '8px', marginBottom: '0' }}>
                    {Array.from(selectedRequests).map(requestId => {
                      const request = pendingRequests.find(r => r.id === requestId);
                      if (request) {
                        const displayName = getRequestDisplayName(request);
                        return (
                          <li key={requestId} style={{ marginBottom: '4px' }}>
                            {displayName} ({request.organization_name})
                          </li>
                        );
                      }
                      return null;
                    })}
                  </ul>
                </div>
              )}
            </DialogContent>
            <DialogActions>
              <Button
                appearance="secondary"
                data-testid="bulk-delete-cancel-button"
                disabled={isBulkDeleting}
                onClick={() => setBulkDeleteDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button
                appearance="primary"
                data-testid="bulk-delete-confirm-button"
                disabled={isBulkDeleting}
                onClick={handleConfirmBulkDelete}
                icon={isBulkDeleting ? <Spinner size="tiny" /> : undefined}
              >
                {isBulkDeleting ? 'Deleting...' : 'Delete'}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      {/* Single Delete Confirmation Dialog (from actions menu) */}
      <Dialog open={singleDeleteDialogOpen} onOpenChange={(_, data) => {
        if (!data.open && !isSingleDeleting) {
          setSingleDeleteDialogOpen(false);
          setTenantToDelete(null);
        }
      }}>
        <DialogSurface data-testid="single-delete-dialog">
          <DialogBody>
            <DialogTitle>Confirm Delete</DialogTitle>
            <DialogContent>
              <Text>
                Are you sure you want to delete <strong>{tenantToDelete?.tenant_name}</strong>? This action cannot be undone.
                Both enterprise apps will be removed from Azure AD.
              </Text>
            </DialogContent>
            <DialogActions>
              <Button
                appearance="secondary"
                data-testid="single-delete-cancel-button"
                disabled={isSingleDeleting}
                onClick={() => {
                  setSingleDeleteDialogOpen(false);
                  setTenantToDelete(null);
                }}
              >
                Cancel
              </Button>
              <Button
                appearance="primary"
                data-testid="single-delete-confirm-button"
                disabled={isSingleDeleting}
                onClick={handleConfirmSingleDelete}
                icon={isSingleDeleting ? <Spinner size="tiny" /> : undefined}
              >
                {isSingleDeleting ? 'Deleting...' : 'Delete'}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      {/* Admin Consent Modal */}
      {adminConsentTenant && adminConsentApps.length > 0 && (
        <AdminConsentModal
          isOpen={adminConsentDialogOpen}
          tenantId={adminConsentTenant.tenant_id}
          tenantName={adminConsentTenant.tenant_name}
          apps={adminConsentApps}
          onVerifySuccess={handleAdminConsentVerifySuccess}
          onClose={handleCloseAdminConsentDialog}
        />
      )}

      {/* Edit Tenant Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={(_, data) => {
        if (!data.open) {
          setEditDialogOpen(false);
          setTenantToEdit(null);
        }
      }}>
        <DialogSurface data-testid="edit-tenant-dialog">
          <DialogBody>
            <DialogTitle>Edit Tenant</DialogTitle>
            <DialogContent>
              {tenantToEdit && (
                <TenantRegistrationForm
                  initialData={{
                    tenant_id: tenantToEdit.tenant_id,
                    tenant_name: tenantToEdit.tenant_name,
                    domain: tenantToEdit.domain,
                    notes: tenantToEdit.notes || '',
                  }}
                  editMode={true}
                  onSuccess={handleEditSuccess}
                  onCancel={() => {
                    setEditDialogOpen(false);
                    setTenantToEdit(null);
                  }}
                />
              )}
            </DialogContent>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      {/* Tenant Fulfillment Modal */}
      {fulfillmentRequest && (
        <TenantFulfillmentModal
          isOpen={fulfillmentModalOpen}
          request={fulfillmentRequest}
          onClose={() => {
            setFulfillmentModalOpen(false);
            setFulfillmentRequest(null);
          }}
          onSave={handleSaveChecklist}
          onComplete={handleCompleteFulfillment}
        />
      )}

      {/* Add Tenant Dialog */}
      <Dialog open={showAddDialog} onOpenChange={(_, data) => {
        setShowAddDialog(data.open);
        if (!data.open) {
          setFulfillingRequestId(null);
          setFulfillmentPrefillData(null);
        }
      }}>
        <DialogSurface>
          <DialogBody>
            <DialogTitle>
              {fulfillmentPrefillData ? 'Create Managed Tenant' : 'Register New Tenant'}
            </DialogTitle>
            <DialogContent>
              <TenantRegistrationForm
                onSuccess={handleRegistrationSuccess}
                onCancel={() => {
                  setShowAddDialog(false);
                  setFulfillingRequestId(null);
                  setFulfillmentPrefillData(null);
                }}
                initialData={fulfillmentPrefillData || undefined}
              />
            </DialogContent>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </PageLayout>
  );
};
